import React, { useState } from 'react';
import { QRHealthReport } from '@/types/qr';
import { ShieldCheck, AlertTriangle, AlertCircle, Info, ChevronRight, Activity } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Badge } from '@/components/ui/Badge';

interface QRHealthIndicatorProps {
  report: QRHealthReport;
}

export const QRHealthIndicator: React.FC<QRHealthIndicatorProps> = ({ report }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const statusConfig = {
    excellent: {
      badge: <Badge variant="success" size="sm"><ShieldCheck className="h-3 w-3" /> Scannable (100%)</Badge>,
      color: 'text-success',
      label: 'Excellent Optical Scanability'
    },
    good: {
      badge: <Badge variant="purple" size="sm"><ShieldCheck className="h-3 w-3" /> Scannable ({report.score}%)</Badge>,
      color: 'text-primary',
      label: 'Good Optical Scanability'
    },
    warning: {
      badge: <Badge variant="warning" size="sm"><AlertTriangle className="h-3 w-3" /> Needs Attention ({report.score}%)</Badge>,
      color: 'text-warning',
      label: 'Minor Scan Issues'
    },
    critical: {
      badge: <Badge variant="critical" size="sm"><AlertCircle className="h-3 w-3" /> Low Reliability ({report.score}%)</Badge>,
      color: 'text-destructive',
      label: 'High Scan Failure Risk'
    }
  };

  const current = statusConfig[report.status];
  const isHealthy = report.status === 'excellent' || report.status === 'good';
  const simpleBadge = isHealthy ? (
    <Badge variant="success" size="sm"><ShieldCheck className="h-3 w-3" /> Good</Badge>
  ) : (
    <Badge variant="warning" size="sm"><AlertTriangle className="h-3 w-3" /> Needs Attention</Badge>
  );

  return (
    <>
      <button
        onClick={() => setIsModalOpen(true)}
        className="group flex items-center justify-between w-full p-3 rounded-xl bg-muted hover:bg-secondary border border-border transition-all cursor-pointer text-left active:scale-95 shadow-xs"
      >
        <div className="flex items-center gap-2.5">
          <Activity className={`h-4 w-4 ${isHealthy ? 'text-success' : 'text-warning'}`} />
          <div>
            <span className="text-[12px] font-semibold text-foreground block tracking-tight">
              Scanability Health
            </span>
            <span className="text-[11px] text-muted-foreground">
              Will this QR code scan reliably?
            </span>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          {simpleBadge}
          <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:translate-x-0.5 transition-transform" />
        </div>
      </button>

      {/* Health Diagnostics Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="QR Code Health Diagnostics"
        description="Real-time optical scanability analysis and contrast breakdown"
      >
        <div className="space-y-4 pt-1">
          {/* Health Score Header */}
          <div className="p-4 rounded-xl bg-muted border border-border flex items-center justify-between">
            <div>
              <p className="text-[11px] text-muted-foreground uppercase tracking-tight font-semibold">Overall Optical Score</p>
              <h4 className={`text-[28px] font-semibold tracking-tight mt-0.5 ${current.color}`}>{report.score} / 100</h4>
              <p className="text-[14px] text-foreground mt-1">{current.label}</p>
            </div>
            <div className="text-right space-y-1 text-[12px]">
              <div className="text-muted-foreground">
                Contrast Ratio: <span className="font-mono font-medium text-foreground">{report.contrastRatio.toFixed(1)}:1</span>
              </div>
              <div className="text-muted-foreground">
                Payload Size: <span className="font-mono text-foreground">{report.dataLength} bytes</span>
              </div>
              <div className="text-muted-foreground">
                Est. Grid Version: <span className="font-mono text-foreground">v{report.estimatedVersion}</span>
              </div>
            </div>
          </div>

          {/* Issues / Recommendations List */}
          <div className="space-y-2.5">
            <h5 className="text-[12px] font-semibold uppercase tracking-tight text-muted-foreground">
              Audit Findings & Recommendations
            </h5>

            {report.issues.length === 0 ? (
              <div className="p-4 rounded-xl bg-success/10 border border-success/20 text-success text-[14px] flex items-center gap-3">
                <ShieldCheck className="h-5 w-5 shrink-0" />
                <span>All scanability checks passed! High optical contrast, adequate quiet zone, and balanced data density.</span>
              </div>
            ) : (
              report.issues.map((issue) => (
                <div
                  key={issue.id}
                  className={`p-3.5 rounded-xl border text-[14px] space-y-1.5 ${
                    issue.level === 'critical'
                      ? 'bg-destructive/10 border-destructive/20 text-destructive'
                      : issue.level === 'warning'
                      ? 'bg-warning/10 border-warning/20 text-warning'
                      : 'bg-primary/10 border-primary/20 text-primary'
                  }`}
                >
                  <div className="flex items-center gap-2 font-semibold">
                    {issue.level === 'critical' ? (
                      <AlertCircle className="h-4 w-4" />
                    ) : issue.level === 'warning' ? (
                      <AlertTriangle className="h-4 w-4" />
                    ) : (
                      <Info className="h-4 w-4" />
                    )}
                    <span>{issue.title}</span>
                  </div>
                  <p className="leading-relaxed text-[12px] opacity-90">{issue.description}</p>
                  {issue.recommendation && (
                    <p className="font-normal text-[12px] pt-1 border-t border-current/10">
                      Tip: {issue.recommendation}
                    </p>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </Modal>
    </>
  );
};
